/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { useEffect, useState } from 'react';

import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { NODE_E2E_CONFIG } from '@/config/embeddedApps';
import { useEmbeddedApp } from '@/hooks/useEmbeddedApp';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

type AuthState = 'loading' | 'authenticated' | 'error';

function getMimicApiUrl(): string {
  return window.mimicConfig.MIMIC_MGMT_PLANE_API as string;
}

export function NodeE2EPage() {
  const { t } = useMimicTranslation('global');
  const [authState, setAuthState] = useState<AuthState>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const selectedTenantID = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const { iframeRef, iframeSrc, env } = useEmbeddedApp(NODE_E2E_CONFIG);

  const nodeE2EBaseUrl = NODE_E2E_CONFIG.getUrl(env);
  const nodeE2EOrigin = nodeE2EBaseUrl ? new URL(nodeE2EBaseUrl).origin : '';

  useEffect(() => {
    if (!nodeE2EBaseUrl) {
      setErrorMessage('Node E2E is not available in this environment');
      setAuthState('error');
      return;
    }

    async function authenticate() {
      try {
        const { data: tokenInfo } = await mgmtPlaneAPIClient.auth.generateToken({ maxAge: 15 });

        const body: Record<string, string> = {
          sessionId: tokenInfo.token,
          env,
          ...(selectedTenantID ? { tenantId: selectedTenantID } : {}),
        };

        if (env === 'local' || env === 'localhost') {
          body.apiUrl = getMimicApiUrl();
        }

        const response = await fetch(`${nodeE2EOrigin}/api/auth/sso`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });

        if (response.ok) {
          setAuthState('authenticated');
        } else {
          const data = await response.json().catch(() => ({}));
          setErrorMessage(data.message || `Authentication failed (${response.status})`);
          setAuthState('error');
        }
      } catch (err) {
        setErrorMessage('Failed to authenticate');
        setAuthState('error');
      }
    }

    authenticate();
  }, [env, nodeE2EBaseUrl, nodeE2EOrigin, selectedTenantID]);

  if (authState === 'loading') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>{t('loading')}...</div>
    );
  }

  if (authState === 'error') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, color: 'red' }}>
        {errorMessage}
      </div>
    );
  }

  return (
    <iframe
      ref={iframeRef}
      src={iframeSrc}
      allow="clipboard-read; clipboard-write; fullscreen"
      style={{
        width: '100%',
        flex: 1,
        border: 'none',
      }}
      title="Node E2E VM Management"
    />
  );
}
